import { ItemValue, isUndefined, isArray, Item, ItemsMap } from '@/packages/selection-controller/general/main'

export type Direction = 'asc' | 'desc'
export type DirectionsPath<D extends string = Direction> = Record<D | 'null', D | null>
export type NextResolver<T = Direction> = (value: T | null) => T | null

const ASC = 'asc'
const DESC = 'desc'
const DIRECTIONS_PATH: DirectionsPath = {
  null: ASC,
  [ASC]: DESC,
  [DESC]: null,
}

/**
 * Resolves next direction from declared paths map(key - from, value - to).
 *
 * `null` value - is the end of path traversing and field should be removed.
 *
 * @category Functions
 * @export
 * @template D - Directions type.
 * @param path - Key-value path.
 * @param current - Current direction value.
 * @returns New direction or `null`.
 */
export function pathNextResolver<D extends string = Direction>(
  path: DirectionsPath<D>,
  current: D | null,
): D | null {
  return path[current as unknown as D]
}


/**
 * `pathNextResolver` Wrapper that uses the default path to find out
 * which direction will be next.
 *
 * @category Functions
 * @export
 * @param current - Current direction value.
 * @returns New direction or `null`.
 */
export function defaultNextResolver(current: Direction | null) {
  return pathNextResolver(DIRECTIONS_PATH, current)
}

/**
 * Ordering value update mechanics.
 *
 * @export
 * @template D - Directions type.
 * @template K - Field type.
 * @param nextResolver - Next direction resolver function that receives
 *    current direction and returns new one or null if this was the
 *    last direction in a line.
 * @param multiple - Whether there can be multiple orderings at the same time.
 * @param current - Current ordering value.
 * @param needle - New value. Might be a field name or a list of new orderings.
 * @returns Updated ordering.
 */
export function updateValue<D extends string = Direction, K = ItemValue>(
  nextResolver: NextResolver<D>,
  multiple: boolean,
  current: ItemsMap<D>,
  needle: K | Item<D>[],
): ItemsMap<D> {
  if (isArray(needle)) {
    return new ItemsMap(multiple || needle.length <= 1 ? needle : [needle[0]])
  }

  const state = current.get(needle)
  const has = !isUndefined(state)
  const next = nextResolver(has ? state.value : null)

  if (!multiple) {
    if (next === null) return new ItemsMap([])

    return new ItemsMap([{ key: needle, value: next }])
  }

  let result = current.items.slice()
  const index = current.indexMap[needle] as unknown as number

  if (next === null) {
    if (has) result.splice(index, 1)
  } else {
    const value = { key: needle, value: next }

    if (has) {
      result[index] = value
    } else {
      result.push(value)
    }
  }

  return new ItemsMap(result)
}
